/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';

import Icon from '_components/atomic/Icon';
import Button from '_components/atomic/Button';

const { colors, devices, fontSizes, spaces } = theme;

const StyledBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  max-width: ${spaces.large24X * 12}px;
  width: 100%;

  position: relative;

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: ${colors.indigo[25]};
    border-bottom-left-radius: 100px;
    border-bottom-right-radius: 100px;
    z-index: -2;
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 900px;
    background: ${`url(${config.basePath}/images/llc-incorporation-banner-desktop-2.png) no-repeat`};
    border-bottom-right-radius: 100px;
    z-index: -1;
  }

  @media ${devices.tablet} {
    &::before {
      border-bottom-left-radius: unset;
      border-bottom-right-radius: unset;
    }

    &::after {
      display: none;
    }
  }
`;

const StyledBannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${spaces.large24X * 10 + 80}px;
  width: 100%;
  padding-top: 64px;
  margin-bottom: 23px;

  @media ${devices.tablet} {
    padding-top: 0;
    margin-bottom: 0;
  }
`;

const StyledContentBlock = styled(
  forwardRef(({ desktop, mobile, noPadding, noMargin, ...props }, ref) => (
    <div ref={ref} {...props} />
  ))
)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  width: 45%;
  margin-bottom: ${spaces.large6X}px;

  ${({ noPadding }) =>
    noPadding &&
    css`
      padding: 0;
    `}

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${devices.tablet} {
    width: 100%;
    padding-top: ${spaces.large}px;
    padding-left: ${spaces.regular}px;
    padding-right: ${spaces.regular}px;
    margin-bottom: ${spaces.large}px;

    button {
      width: 100%;
      justify-content: center;
      margin-bottom: ${spaces.small}px;
    }

    ${({ noPadding }) =>
      noPadding &&
      css`
        padding: 0;
      `}

    ${({ noMargin }) =>
      noMargin &&
      css`
        margin: 0;
      `}

    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: flex;
      `}
  }
`;

const StyledImageContainer = styled(
  forwardRef(({ fullWidth, ...props }, ref) => <div ref={ref} {...props} />)
)`
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      img {
        width: 100%;
      }
    `}
`;

const StyledTitle = styled.h1`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 700;
  font-size: ${fontSizes.large9X}px;
  line-height: 130%;
  text-align: left;
  color: ${colors.black};
  span {
    color: ${colors.indigo[650]};
  }

  @media ${devices.tablet} {
    font-size: ${fontSizes.large2X}px;
    line-height: 140%;
  }
`;

const StyledText = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.regular}px;
  line-height: 150%;
  color: ${colors.gray[900]};
  width: 100%;
  ul {
    margin: 0px;
    padding-left: 1.2rem;
    line-height: 175%;
  }
  ul li::marker {
    color: ${colors.indigo[650]};
  }
  ul li span {
    font-size: ${fontSizes.regular}px;
  }
  @media ${devices.tablet} {
    font-size: ${fontSizes.regular}px;
    ul {
      padding-left: 0.9rem;
    }
    ul li span {
      font-size: ${fontSizes.smallX}px;
    }
  }
`;
const StyledLogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  picture {
    width: 25%;
  }
  picture:first-child {
    width: 17%;
  }
  img {
    width: 100%;
  }

  @media ${devices.tablet} {
    img {
      width: 100%;
    }
  }
`;
const StyledFormLLCContainer = styled.div`
  max-width: 463px;
  background-color: ${colors.white};
  border-radius: 10px;
  box-shadow: 0px 4px 5px 0px #00000040;
  padding-left: 32px;
  padding-right: 32px;
  padding-top: 16px;
  padding-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;

  @media ${devices.tablet} {
    gap: 16px;
    padding: 30px;
    padding-bottom: 16px;
    padding-top: 20px;
    max-width: 100%;
  }
`;
const StyledTestimonialContainer = styled.div`
  position: absolute;

  max-width: 250px;

  right: 32%;
  bottom: ${spaces.largeX}px;

  padding: ${spaces.small}px;

  background-color: ${colors.white};
  border-radius: 10px;
  box-shadow: 0px 4px 5px 0px #00000040;

  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.smallX}px;
  line-height: 18px;
  color: ${colors.gray[900]};

  z-index: 1;

  @media ${devices.tablet} {
    max-width: 158px;

    left: ${spaces.large2X}px;
    bottom: ${spaces.medium}px;

    padding: ${spaces.small2X}px;

    font-size: ${fontSizes.smallest}px;
    line-height: 12px;
    text-align: center;
  }
`;

const LLCProductBannerSection = ({ price, onConfirm }) => {
  return (
    <StyledBanner>
      <StyledBannerContainer>
        <StyledContentBlock>
          <StyledFormLLCContainer>
            <StyledTitle>
              Form Your Business in Just a <span>Few Minutes</span>
            </StyledTitle>
            <StyledText>
              <ul>
                <li>
                  LLC Formations Starting at ${price} <span>+ state fees</span>
                </li>
                <li>Join Over 1,000,000 Small Businesses LLCs</li>
                <li>Custom Member Dashboard to Mange Your Business</li>
                <li>Money-back Satisfaction Guarantee</li>
              </ul>
            </StyledText>
            <Button color={colors.indigo[650]} onClick={onConfirm}>
              Start Forming Your LLC
            </Button>
            <StyledLogoContainer>
              <picture>
                <source
                  srcSet={`${config.basePath}/images/us-1.svg`}
                  type="image/svg+xml"
                />
                <img src={`${config.basePath}/images/us-1.svg`} alt="" />
              </picture>
              <picture>
                <source
                  srcSet={`${config.basePath}/images/money-back-logo.svg`}
                  type="image/svg+xml"
                />
                <img
                  src={`${config.basePath}/images/money-back-logo.svg`}
                  title="Money Back Guarantee Logo"
                  alt="Money Back Guarantee Logo"
                />
              </picture>
              <picture>
                <source
                  srcSet={`${config.basePath}/images/bbb.svg`}
                  type="image/svg+xml"
                />
                <img
                  src={`${config.basePath}/images/bbb.svg`}
                  title="Better Business Bureau Logo"
                  alt="Better Business Bureau Logo"
                />
              </picture>
            </StyledLogoContainer>
          </StyledFormLLCContainer>
        </StyledContentBlock>
        <StyledContentBlock mobile noPadding noMargin>
          <StyledImageContainer fullWidth>
            <picture>
              <source
                srcSet={`${config.basePath}/images/llc-incorporation-banner-mobile-2.png`}
                type="image/png"
              />
              <img
                src={`${config.basePath}/images/llc-incorporation-banner-mobile-2.png`}
                title="Business owner working"
                alt="Business owner working"
              />
            </picture>
          </StyledImageContainer>
        </StyledContentBlock>
      </StyledBannerContainer>
      <StyledTestimonialContainer>
        “Incorpified was a huge help with getting my new business incorporated.
        I can’t wait to start growing my newly formed LLC and have Incorpified
        by my side for all my business needs”
        <br />
        <br />
        Cynthia Torres
        <br />
        <b>Incorpified Customer {new Date().getFullYear()}</b>
      </StyledTestimonialContainer>
    </StyledBanner>
  );
};

LLCProductBannerSection.propTypes = {
  price: PropTypes.number,
  onConfirm: PropTypes.func,
};

export default LLCProductBannerSection;
